/**
 * ItemSelectionStep.js
 *
 * Step 2 of the clearance wizard.
 * Allows selection of brands and sizes for each required item.
 * Adapted from ItemSelectionModal.js with wizard-style navigation.
 *
 * @component
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Select,
  InputNumber,
  Button,
  Space,
  Typography,
  Divider,
  Card,
  Alert,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import Utilities from "../../../../dependencies/custom/react-utilities";
import domainSettings from "../../../../dependencies/custom/settings";

const { Text, Title } = Typography;
const { Option } = Select;

/**
 * ItemSelectionStep Component
 *
 * @param {object} clearance - The clearance data with requirement_items
 * @param {function} onNext - Callback to proceed to next step
 * @param {function} onPrevious - Callback to go back
 * @param {function} onRefresh - Callback to refresh clearance data
 * @param {boolean} canGoBack - Whether back navigation is allowed
 */
const ItemSelectionStep = ({
  clearance,
  onNext,
  onPrevious,
  onRefresh,
  canGoBack,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selections, setSelections] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [saving, setSaving] = useState(false);

  /**
   * Initialize form with existing selections or defaults
   */
  useEffect(() => {
    if (clearance) {
      const initialSelections = {};

      clearance.requirement_items?.forEach((item, index) => {
        // Find existing selection for this item
        const existingSelection = clearance.selections?.find(
          (sel) =>
            sel.bot_item === item.bot_item || sel.item_name === item.item_name,
        );

        if (existingSelection) {
          initialSelections[index] = {
            bot_brand: existingSelection.bot_brand,
            size_variant: existingSelection.size_variant,
            quantity: existingSelection.quantity,
            unit_price: existingSelection.unit_price,
          };

          form.setFieldValue(
            ["selections", index, "bot_brand"],
            existingSelection.bot_brand,
          );
          form.setFieldValue(
            ["selections", index, "size_variant"],
            existingSelection.size_variant,
          );
          form.setFieldValue(
            ["selections", index, "quantity"],
            existingSelection.quantity,
          );
        } else {
          initialSelections[index] = {
            quantity: item.quantity,
            unit_price: 0,
          };
          form.setFieldValue(["selections", index, "quantity"], item.quantity);
        }
      });

      setSelections(initialSelections);
    }
  }, [clearance, form]);

  /**
   * Calculate total amount whenever selections change
   */
  useEffect(() => {
    const total = Object.values(selections).reduce((sum, sel) => {
      return sum + (sel?.unit_price || 0) * (sel?.quantity || 0);
    }, 0);
    setTotalAmount(total);
  }, [selections]);

  /**
   * Handle brand selection change
   */
  const handleBrandChange = (index, brandId, item) => {
    const selectedBrand = item.available_brands?.find((b) => b.id === brandId);

    if (selectedBrand) {
      setSelections((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          bot_brand: brandId,
          unit_price: parseFloat(selectedBrand.price),
          quantity: prev[index]?.quantity || item.quantity,
        },
      }));
    }
  };

  /**
   * Handle size selection change
   */
  const handleSizeChange = (index, sizeId) => {
    setSelections((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        size_variant: sizeId,
      },
    }));
  };

  /**
   * Handle quantity change
   */
  const handleQuantityChange = (index, quantity) => {
    setSelections((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        quantity: quantity,
      },
    }));
  };

  /**
   * Calculate line total for an item
   */
  const calculateLineTotal = (index) => {
    const selection = selections[index];
    if (!selection) return 0;
    return (selection.unit_price || 0) * (selection.quantity || 0);
  };

  /**
   * Save selections to the API
   */
  const handleSave = async () => {
    try {
      await form.validateFields();

      // Map and filter selections
      const selectionsArray = clearance.requirement_items
        .map((item, index) => {
          const selection = selections[index];

          // Skip optional items without brand selected
          if (!item.is_mandatory && !selection?.bot_brand) {
            return null;
          }

          return {
            bot_item: item.bot_item || item.id,
            bot_brand: selection.bot_brand,
            size_variant: selection.size_variant || null,
            quantity: selection.quantity,
          };
        })
        .filter((item) => item !== null);

      setSaving(true);

      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.bot.portalSelection,
        { botpc: clearance.botpc, selections: selectionsArray },
        navigate,
      );

      if (response.ok) {
        message.success("Selections saved successfully");
        await onRefresh();
        onNext();
      } else {
        Utilities.showError(response, "Failed to save selections");
      }
    } catch (error) {
      console.error("Save error:", error);
      message.error("Please complete all required fields");
    } finally {
      setSaving(false);
    }
  };

  if (!clearance) return null;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          <ShoppingCartOutlined style={{ marginRight: 8 }} />
          Select Items
        </Title>
        <Text type="secondary">
          Choose a brand and quantity for each required item. Optional items can
          be skipped.
        </Text>
      </div>

      {/* Instructions */}
      <Alert
        message="Selection Instructions"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Select a brand for each mandatory item</li>
            <li>Choose a size if the item requires it</li>
            <li>Quantity cannot be less than the required amount</li>
            <li>Optional items can be left unselected</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* Item Selection Form */}
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {clearance.requirement_items?.map((item, index) => (
            <Card
              key={item.id || index}
              size="small"
              title={
                <Space>
                  <Text strong>{item.item_name}</Text>
                  {!item.is_mandatory && (
                    <Text type="secondary">(Optional)</Text>
                  )}
                </Space>
              }
              extra={<Text type="secondary">Required: {item.quantity}</Text>}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {/* Brand Selection */}
                <Form.Item
                  label="Select Brand"
                  name={["selections", index, "bot_brand"]}
                  rules={[
                    {
                      required: item.is_mandatory,
                      message: "Please select a brand",
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <Select
                    placeholder={
                      item.is_mandatory
                        ? "Select brand (required)"
                        : "Select brand (optional)"
                    }
                    onChange={(brandId) =>
                      handleBrandChange(index, brandId, item)
                    }
                    size="large"
                    allowClear={!item.is_mandatory}
                  >
                    {item.available_brands?.map((brand) => (
                      <Option
                        key={brand.id}
                        value={brand.id}
                        disabled={brand.stock_quantity <= 0}
                      >
                        {brand.name} - UGX{" "}
                        {parseFloat(brand.price).toLocaleString()}
                        {brand.stock_quantity <= 0 && " (Out of Stock)"}
                        {brand.stock_quantity > 0 &&
                          brand.stock_quantity < 10 &&
                          ` (Only ${brand.stock_quantity} left)`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Size Selection (if applicable) */}
                {item.has_size && item.available_sizes?.length > 0 && (
                  <Form.Item
                    label="Select Size"
                    name={["selections", index, "size_variant"]}
                    style={{ marginBottom: 12 }}
                  >
                    <Select
                      placeholder="Select size"
                      onChange={(sizeId) => handleSizeChange(index, sizeId)}
                      size="large"
                      allowClear
                    >
                      {item.available_sizes.map((size) => (
                        <Option key={size.id} value={size.id}>
                          {size.size_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {/* Quantity */}
                <Form.Item
                  label={`Quantity (minimum: ${item.quantity})`}
                  name={["selections", index, "quantity"]}
                  rules={[
                    {
                      required: item.is_mandatory,
                      message: "Please enter quantity",
                    },
                    {
                      type: "number",
                      min: item.quantity,
                      message: `Quantity must be at least ${item.quantity}`,
                    },
                  ]}
                  style={{ marginBottom: 12 }}
                >
                  <InputNumber
                    min={item.quantity}
                    style={{ width: "100%" }}
                    onChange={(qty) => handleQuantityChange(index, qty)}
                    size="large"
                  />
                </Form.Item>

                {/* Line Total */}
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#e6f7ff",
                    borderRadius: 4,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Text>Line Total:</Text>
                  <Text strong>
                    UGX {calculateLineTotal(index).toLocaleString()}
                  </Text>
                </div>
              </Space>
            </Card>
          ))}
        </Space>
      </Form>

      <Divider />

      {/* Grand Total */}
      <div
        style={{
          padding: 16,
          background: "#f0f2f5",
          borderRadius: 8,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Text>Total Items: {clearance.requirement_items?.length || 0}</Text>
          </div>
          <div>
            <Text style={{ fontSize: 18 }}>Grand Total: </Text>
            <Text strong style={{ fontSize: 24, color: "#1890ff" }}>
              UGX {totalAmount.toLocaleString()}
            </Text>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={onPrevious}
          disabled={!canGoBack}
        >
          Back
        </Button>
        <Button
          type="primary"
          size="large"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
        >
          Save & Continue
        </Button>
      </div>
    </div>
  );
};

export default ItemSelectionStep;
